// src/auth/AuthContext.jsx
// Single source of truth for Supabase auth state + the shared login modal.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { supabase } from "../utils/supabaseClient";
import LoginModal from "./LoginModal";

const AuthContext = createContext(null);

// supabase-js persists the session in localStorage under `sb-<ref>-auth-token`
// (plus `-code-verifier` / `-user` siblings). We clear these by hand on sign-out
// because GoTrueClient._signOut() skips its own cleanup when the /logout call
// fails — see the comment in signOut() below.
function clearPersistedSession() {
  try {
    const key = supabase.auth.storageKey;
    const keys = key
      ? [key, `${key}-code-verifier`, `${key}-user`]
      : Object.keys(window.localStorage).filter(
          (k) => k.startsWith("sb-") && k.includes("auth-token")
        );
    keys.forEach((k) => window.localStorage.removeItem(k));
  } catch (err) {
    // localStorage can throw in private-browsing modes; the in-memory reset
    // in signOut() still logs the user out for this page load.
  }
}

// signOut() can hang indefinitely: it waits on _acquireLock(), which another
// tab may be holding. Resolve with an error instead of leaving the UI stuck.
function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((resolve) =>
      setTimeout(() => resolve({ error: new Error(`signOut timed out after ${ms}ms`) }), ms)
    ),
  ]);
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!isMounted) return;
        setSession(data ? data.session : null);
        setLoading(false);
      })
      .catch(() => {
        if (!isMounted) return;
        setSession(null);
        setLoading(false);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (!isMounted) return;
      setSession(newSession);
      setLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signInWithEmail = useCallback(async (email) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          // If the email template sends a magic link (instead of / alongside
          // the {{ .Token }} code), return to wherever the user currently is.
          emailRedirectTo: window.location.origin,
        },
      });
      return { error };
    } catch (err) {
      return { error: err };
    }
  }, []);

  const verifyCode = useCallback(async (email, code) => {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: code,
        type: "email",
      });
      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }, []);

  const signOut = useCallback(async () => {
    let error = null;
    try {
      // scope: 'local' revokes only this browser's session. The default
      // ('global') revokes every session for the user, which is a heavier
      // server call and more ways for the request to fail.
      const res = await withTimeout(supabase.auth.signOut({ scope: "local" }), 5000);
      error = res ? res.error : null;
    } catch (err) {
      error = err;
    }

    // GoTrueClient._signOut() returns *before* clearing storage or emitting
    // SIGNED_OUT whenever the /logout request fails with anything other than
    // 401/403/404 — which includes 5xx, rate limits, and plain network errors
    // (an AuthRetryableFetchError is not an AuthApiError, so it isn't in the
    // ignore list). The session then survives and the click looks like a no-op.
    // Tear the session down locally regardless of what the server said.
    if (error) {
      console.error(
        "signOut: server logout failed, clearing session locally:",
        error.message || error
      );
    }
    clearPersistedSession();
    setSession(null);

    return { error };
  }, []);

  const openLogin = useCallback(() => setLoginOpen(true), []);
  const closeLogin = useCallback(() => setLoginOpen(false), []);

  const value = useMemo(
    () => ({
      user: session ? session.user : null,
      session,
      loading,
      signInWithEmail,
      verifyCode,
      signOut,
      openLogin,
      closeLogin,
    }),
    [session, loading, signInWithEmail, verifyCode, signOut, openLogin, closeLogin]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {loginOpen && <LoginModal onClose={closeLogin} />}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
